/**
 * Image Analysis API
 * Provides functionality for analyzing images using GPT-4o Vision capabilities.
 * Now uses secure backend proxy instead of direct OpenAI API calls.
 */

import { OpenAIProxyService } from '@/services/api/openaiProxyApi';
import { isDevEnvironment } from '@/utils/environment';

// Interface for analysis response
interface ImageAnalysisResponse {
  description: string;
  detectedItems: string[];
  storeRelevance: 'high' | 'medium' | 'low';
  error?: string;
}

/**
 * Analyzes an image using GPT-4o Vision capabilities
 * @param imageUrl The URL of the image to analyze
 * @param prompt Optional custom prompt to guide the image analysis
 * @returns Detailed analysis of the image content
 */
export async function analyzeImage(
  imageUrl: string, 
  prompt?: string
): Promise<ImageAnalysisResponse> {
  try {
    const openaiProxy = new OpenAIProxyService();
    
    const response = await openaiProxy.analyzeImage(imageUrl, prompt);
    
    return response;
  } catch (error) {
    console.error('Error analyzing image:', error);
    
    // Return a graceful fallback in development environments
    if (isDevEnvironment()) {
      return {
        description: 'This is a simulated image analysis description for development. In production, this would contain a detailed description of the store image generated by GPT-4o.',
        detectedItems: ['sample product display', 'store layout', 'promotional signage'],
        storeRelevance: 'high',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
    
    // In production, propagate the error
    throw error;
  }
}

/**
 * Save OpenAI API key (deprecated - API keys are now managed on the backend)
 */
export async function saveOpenAIApiKey(apiKey: string): Promise<void> {
  console.warn('saveOpenAIApiKey is deprecated - API keys are now managed securely on the backend');
  // This function is now a no-op since API keys are managed on the backend
} 